import { MenuPlacement, Props } from "react-select";

import { dropdownVariants, sizeVariants } from "./variants";
import { IconName, IconSize, TemplateIconsName } from "../Icon";

export type Option = {
  value: string;
  label: string;
  isDisabled?: boolean;
  iconName?: IconName | TemplateIconsName;
  iconSize?: IconSize;
};

export type OptionGroup = {
  label: string;
  options: Option[];
};

export type DropdownVariant = typeof dropdownVariants[number];
export type SizeVariant = typeof sizeVariants[number];

export type DropdownProps = Props & {
  testId?: string;
  label?: string;
  variant?: DropdownVariant;
  /** Will render a required indicator next to the label when true. Does not affect behavior of the component. */
  isRequired?: boolean;
  /** When supplied, the component will be rendered in an error state. */
  errorMessage?: string;
  options: Option[] | OptionGroup[];
  /** Max number of options to show in the menu. It affects the component height when opened.
   * @deprecated Use `maxMenuHeight` and `minMenuHeight` to configure menu height instead
   */
  maxOptionsToShow?: number;
  /** The default value to show when the component is loaded the first time. */
  defaultValue?: Option;
  value?: Option;
  /** The message to the user when there is no selection. */
  placeholder?: string;
  /** Used to identify this dropdown when used in a form. */
  name?: string;
  /** Called when a dropdown option is selected. */
  onSelect: (args: { selected: Option }) => void | string;
  /** Called when the dropdown is cleared. If this prop is not passed, the clear icon will not be rendered. */
  onClear?: (args: { cleared: Option }) => void;
  /** Used to change the direction that the menu opens */
  menuPlacement?: MenuPlacement;
  /** Used to change the size variant of the control */
  size?: SizeVariant;
};

export type MultiSelectDropdownProps = Omit<
  DropdownProps,
  "defaultValue" | "value" | "onClear"
> & {
  defaultValue?: Option[];
  value?: Option[];
  /** Called when a dropdown option is deselected. */
  onDeselect: (args: { deselected: Option }) => void;
  /** Called when the dropdown is cleared. If this prop is not passed, the clear icon will not be rendered. */
  onClear?: (args: { cleared: Option[] }) => void;
};

// Guards

export function isOptionGroupArray(
  options: OptionGroup[]
): options is OptionGroup[] {
  return options.some((option) => option.hasOwnProperty("options"));
}
